// Header: pinned-bar heights, phone menu, "Added to your bag" fly-out. Listeners are delegated or re-bound on
// shopify:section:load, so the theme editor can re-render the header without a reload.
const DE = (window.DE = window.DE || {});
const root = document.documentElement;
const desktop = window.matchMedia('(min-width: 990px)');

/* ---------- Pinned heights ---------- */
// --announcement-h: height of the announcement bar (0 when hidden); --header-h: total height pinned at the top
// (announcement + header bar when sticky, else 0). Later stages pin their toolbars under --header-h.
const isPinned = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).position === 'sticky';
const heightOf = (el) => (el && el.getClientRects().length ? el.getBoundingClientRect().height : 0);

let observer;
const measure = () => {
  const announcement = document.querySelector('[data-announcement]');
  const bar = document.querySelector('[data-header-bar]');
  const announcementHeight = heightOf(announcement);
  let pinned = 0;
  if (isPinned(announcement)) pinned += announcementHeight;
  if (isPinned(bar)) pinned += heightOf(bar);
  root.style.setProperty('--announcement-h', `${Math.round(announcementHeight)}px`);
  root.style.setProperty('--header-h', `${Math.round(pinned)}px`);
};

const observe = () => {
  observer?.disconnect();
  observer = new ResizeObserver(measure);
  document.querySelectorAll('[data-announcement], [data-header-bar]').forEach((el) => observer.observe(el));
  measure();
};

/* ---------- Phone menu ---------- */
let releaseFocus = null;
let hideTimer = 0;
const menu = () => document.getElementById('PhoneMenu');
const opener = () => document.querySelector('[data-menu-open]');

// Menu photos sit in <template>s and are cloned on the first tap, hover or focus of ≡, so they only load then.
const loadThumbs = () => {
  menu()
    ?.querySelectorAll('template[data-menu-thumb]')
    .forEach((template) => template.replaceWith(template.content.cloneNode(true)));
};

const onKeydown = (event) => {
  if (event.key === 'Escape') closeMenu();
};

const openMenu = () => {
  const panel = menu();
  if (!panel || panel.classList.contains('is-open')) return;
  loadThumbs();
  clearTimeout(hideTimer);
  panel.hidden = false;
  panel.getBoundingClientRect(); // start the transition from the closed state
  panel.classList.add('is-open');
  opener()?.setAttribute('aria-expanded', 'true');
  DE.lockScroll();
  releaseFocus = DE.trapFocus(panel, panel.querySelector('[data-menu-close]'));
  document.addEventListener('keydown', onKeydown);
};

const closeMenu = ({ restoreFocus = true } = {}) => {
  const panel = menu();
  if (!panel || !panel.classList.contains('is-open')) return;
  panel.classList.remove('is-open');
  opener()?.setAttribute('aria-expanded', 'false');
  releaseFocus?.();
  releaseFocus = null;
  document.removeEventListener('keydown', onKeydown);
  DE.unlockScroll();
  clearTimeout(hideTimer);
  if (DE.motionOK()) hideTimer = setTimeout(() => (panel.hidden = true), 250);
  else panel.hidden = true;
  if (restoreFocus) opener()?.focus({ preventScroll: true });
};

DE.menu = { open: openMenu, close: closeMenu };

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-menu-open]')) openMenu();
  else if (event.target.closest('[data-menu-close]')) closeMenu();
});

['pointerover', 'focusin', 'touchstart'].forEach((type) => {
  document.addEventListener(
    type,
    (event) => {
      if (event.target instanceof Element && event.target.closest('[data-menu-open]')) loadThumbs();
    },
    { passive: true }
  );
});

// Back/forward cache: a page left through a menu link comes back with the menu closed.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) closeMenu({ restoreFocus: false });
});

// The panel is phone-only: close it if the window grows to the desktop layout. The fly-out is desktop-only.
desktop.addEventListener('change', (event) => {
  if (event.matches) closeMenu({ restoreFocus: false });
  else DE.flyout.close();
  measure();
});

/* ---------- Search ---------- */
// "Search" (desktop) and the magnifier (phone) are links to /search. With the search panel on the page
// (sections/search-panel.liquid) they open it: its script (URL on the panel, assets/predictive-search.js) loads on
// their first hover, focus or tap, else on the click. Without JavaScript, or if the script fails, they stay links.
let searchReady = null;
const loadSearch = () => {
  const script = document.getElementById('SearchPanel')?.dataset.script;
  if (!script) return null;
  searchReady ||= import(script).catch((error) => {
    searchReady = null;
    throw error;
  });
  return searchReady;
};

['pointerover', 'focusin', 'touchstart'].forEach((type) => {
  document.addEventListener(
    type,
    (event) => {
      if (!searchReady && event.target instanceof Element && event.target.closest('[data-open-search]')) {
        loadSearch()?.catch(() => {});
      }
    },
    { passive: true }
  );
});

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[data-open-search]') : null;
  if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const ready = loadSearch();
  if (!ready) return;
  event.preventDefault();
  ready
    .then(() => DE.search.open(link))
    .then((opened) => {
      if (!opened) window.location.assign(link.href);
    })
    .catch(() => window.location.assign(link.href));
});

// Bottom edge of the header bar in the viewport: under the header at the top of the page, 0 once it has scrolled
// away, and never above the pinned bars.
DE.header = {
  bottom() {
    const pinned = parseFloat(root.style.getPropertyValue('--header-h')) || 0;
    const bar = document.querySelector('[data-header-bar]');
    return Math.max(pinned, bar?.getBoundingClientRect().bottom || 0, 0);
  },
};

/* ---------- "Added to your bag" fly-out (desktop) ---------- */
// Opens under the header after every add to bag (DE.cart emits "cart:added"): the added item(s), free-shipping
// progress, "View bag" and Checkout (Direct route only). Closes after the header's "Closes after" seconds (the timer
// pauses while hovered or focused and restarts on each add), with × or Esc. Announced through a live region.
let placeFrame = 0;
let flyoutTimer = 0;
const placeFlyout = () => {
  cancelAnimationFrame(placeFrame);
  placeFrame = requestAnimationFrame(() => DE.flyout.place());
};

const flyoutPaused = (el) => el.matches(':hover') || el.contains(document.activeElement);

const armFlyout = () => {
  clearTimeout(flyoutTimer);
  const el = DE.flyout.el;
  if (!el || el.hidden || flyoutPaused(el)) return;
  const seconds = Math.max(1, Number(el.dataset.seconds) || 7);
  flyoutTimer = setTimeout(() => DE.flyout.close(), seconds * 1000);
};

const bindFlyout = (el) => {
  if (el.dataset.bound) return;
  el.dataset.bound = 'true';
  el.addEventListener('pointerenter', () => clearTimeout(flyoutTimer));
  el.addEventListener('pointerleave', armFlyout);
  el.addEventListener('focusin', () => clearTimeout(flyoutTimer));
  el.addEventListener('focusout', (event) => {
    if (!el.contains(event.relatedTarget)) armFlyout();
  });
};

const announce = (text) => {
  const status = document.querySelector('[data-flyout-status]');
  if (!status) return;
  status.textContent = '';
  setTimeout(() => (status.textContent = text), 50);
};

const flyoutHTML = (el, { items = [], requested = [], cart }) => {
  const strings = DE.config.strings || {};
  const quantities = new Map(requested.map((item) => [Number(item.id), item.quantity]));
  const names = items.map((item) => item.product_title).join(' + ');
  const details = items.map((item) => DE.lineDetail(item)).filter(Boolean).join(' · ');
  const price = items.reduce(
    (sum, item) => sum + item.final_price * (quantities.get(Number(item.variant_id ?? item.id)) || 1),
    0
  );
  const image = items.find((item) => item.image)?.image;
  const count = cart ? cart.item_count : DE.cart?.count() || 0;
  const viewBag = (el.dataset.viewBag || strings.view_bag || 'View bag ([count])').replace('[count]', count);
  const checkout = DE.checkoutButton(el.dataset.checkout, { className: 'added__btn' });
  const photo = image
    ? `<img class="added__img" src="${DE.escape(DE.imageUrl(image, 192))}" srcset="${DE.escape(DE.imageUrl(image, 128))} 128w, ${DE.escape(DE.imageUrl(image, 192))} 192w" sizes="64px" width="64" height="86" alt="">`
    : '<span class="added__img"></span>';
  return `<div class="added__item">
      ${photo}
      <div class="added__text">
        <span>${DE.escape(names)}</span>
        ${details ? `<span class="added__detail">${DE.escape(details)}</span>` : ''}
        <span>${DE.formatMoney(price)}</span>
      </div>
    </div>
    ${cart ? DE.freeShippingHTML(cart.total_price, 'added__shipping') : ''}
    <div class="added__actions${checkout ? '' : ' added__actions--single'}">
      <a class="btn btn--secondary added__btn" href="${DE.escape(DE.config.routes?.cart || '/cart')}" data-open-cart>${DE.escape(viewBag)}</a>
      ${checkout}
    </div>`;
};

DE.flyout = {
  get el() {
    return document.querySelector('[data-flyout]');
  },
  isOpen() {
    const el = this.el;
    return !!el && !el.hidden;
  },
  // Pins the fly-out under the header bar (it is position: fixed).
  place() {
    this.el?.style.setProperty('--flyout-top', `${Math.round(DE.header.bottom())}px`);
  },
  // Shows it in place and follows the header while the page scrolls.
  show() {
    const el = this.el;
    if (!el) return false;
    this.place();
    el.hidden = false;
    window.addEventListener('scroll', placeFlyout, { passive: true });
    window.addEventListener('resize', placeFlyout, { passive: true });
    return true;
  },
  // detail: the "cart:added" detail ({ items, requested, cart }). Returns false when it can't show (phone, turned
  // off in the header settings), so callers can confirm another way.
  open(detail) {
    const el = this.el;
    const body = el?.querySelector('[data-flyout-body]');
    if (!el || !body || !desktop.matches || !detail?.items?.length) return false;
    body.innerHTML = flyoutHTML(el, detail);
    bindFlyout(el);
    this.show();
    announce(el.dataset.announce || DE.config.strings?.added || 'Added to your bag');
    armFlyout();
    return true;
  },
  close() {
    const el = this.el;
    clearTimeout(flyoutTimer);
    window.removeEventListener('scroll', placeFlyout);
    window.removeEventListener('resize', placeFlyout);
    if (!el || el.hidden) return;
    const hadFocus = el.contains(document.activeElement);
    el.hidden = true;
    if (hadFocus) document.querySelector('[data-header-bar] [data-open-cart]')?.focus({ preventScroll: true });
  },
};

// Not while the bag drawer is open (it shows the new line itself). Its "View bag" (data-open-cart) opens the drawer
// through assets/cart.js, which closes the fly-out first.
DE.on('cart:added', (event) => {
  if (event.detail?.options?.flyout === false || DE.cartDrawer?.isOpen()) return;
  DE.flyout.open(event.detail);
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-flyout-close]')) DE.flyout.close();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && !event.defaultPrevented && DE.flyout.isOpen()) DE.flyout.close();
});

/* ---------- Theme editor ---------- */
document.addEventListener('shopify:section:load', () => {
  releaseFocus?.();
  releaseFocus = null;
  document.removeEventListener('keydown', onKeydown);
  if (root.classList.contains('is-scroll-locked')) DE.unlockScroll();
  observe();
  DE.wishlist?.renderCounts();
});

observe();
